import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormBase } from './useFormBase';
import { useDeviceDetection } from './useDeviceDetection';
import { useAmplitudeEvents } from '@/utils/amplitudeEvents';
import { z } from 'zod';

const formatDateForUrlLocal = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseDateFromUrlLocal = (value?: string | null): Date | null => {
  if (!value) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return null;
  const y = Number(m[1]);
  const mm = Number(m[2]);
  const dd = Number(m[3]);
  const date = new Date(y, mm - 1, dd);
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== mm - 1 ||
    date.getDate() !== dd
  ) {
    return null;
  }
  return date;
};

const searchFormSchema = z.object({
  checkIn: z.date().optional(),
  checkOut: z.date().optional(),
  guests: z.string().min(1).default('1'),
});

type SearchFormData = z.infer<typeof searchFormSchema>;

interface UseSearchFormOptions {
  variant?: 'default' | 'booking';
  onDateChange?: (
    checkIn: Date | undefined,
    checkOut: Date | undefined,
  ) => void;
  onGuestsChange?: (guests: string) => void;
  initialCheckIn?: Date;
  initialCheckOut?: Date;
  initialGuests?: string;
}

export const useSearchForm = ({
  variant = 'default',
  onDateChange,
  onGuestsChange,
  initialCheckIn,
  initialCheckOut,
  initialGuests = '1',
}: UseSearchFormOptions = {}) => {
  const navigate = useNavigate();
  const deviceInfo = useDeviceDetection();
  const { trackSearchCompleted } = useAmplitudeEvents();
  const hasInitialized = useRef(false);

  const { form, watch, setValue, getValues } = useFormBase<SearchFormData>({
    schema: searchFormSchema,
    defaultValues: {
      checkIn: initialCheckIn,
      checkOut: initialCheckOut,
      guests: initialGuests,
    },
  });

  const [checkIn, checkOut, guests] = watch(['checkIn', 'checkOut', 'guests']);

  useEffect(() => {
    if (!hasInitialized.current && variant === 'default') {
      const urlParams = new URLSearchParams(window.location.search);
      const urlCheckIn = urlParams.get('checkin');
      const urlCheckOut = urlParams.get('checkout');
      const urlGuests = urlParams.get('guests');

      const parsedIn = parseDateFromUrlLocal(urlCheckIn);
      const parsedOut = parseDateFromUrlLocal(urlCheckOut);

      if (parsedIn) setValue('checkIn', parsedIn);
      if (parsedOut) setValue('checkOut', parsedOut);
      if (urlGuests) setValue('guests', urlGuests);

      hasInitialized.current = true;
    }
  }, [variant, setValue]);

  useEffect(() => {
    if (variant === 'booking') {
      if (initialCheckIn !== checkIn) setValue('checkIn', initialCheckIn);
      if (initialCheckOut !== checkOut) setValue('checkOut', initialCheckOut);
      if (initialGuests !== guests) setValue('guests', initialGuests);
    }
  }, [
    variant,
    initialCheckIn,
    initialCheckOut,
    initialGuests,
    checkIn,
    checkOut,
    guests,
    setValue,
  ]);

  const handleCheckInChange = useCallback(
    (date: Date | undefined) => {
      setValue('checkIn', date);
      onDateChange?.(date, checkOut);
    },
    [setValue, checkOut, onDateChange],
  );

  const handleCheckOutChange = useCallback(
    (date: Date | undefined) => {
      setValue('checkOut', date);
      onDateChange?.(checkIn, date);
    },
    [setValue, checkIn, onDateChange],
  );

  const handleGuestsChange = useCallback(
    (newGuests: string) => {
      try {
        if (deviceInfo.isMobile && deviceInfo.isAndroid) {
          setTimeout(() => {
            setValue('guests', newGuests);
            onGuestsChange?.(newGuests);
          }, 10);
        } else {
          setValue('guests', newGuests);
          onGuestsChange?.(newGuests);
        }
      } catch {
        setValue('guests', '1');
        onGuestsChange?.('1');
      }
    },
    [setValue, deviceInfo.isMobile, deviceInfo.isAndroid, onGuestsChange],
  );

  const handleSearch = useCallback(() => {
    const formData = getValues();
    trackSearchCompleted(formData.checkIn, formData.checkOut, formData.guests);

    const searchParams = new URLSearchParams();
    if (formData.checkIn)
      searchParams.set('checkin', formatDateForUrlLocal(formData.checkIn));
    if (formData.checkOut)
      searchParams.set('checkout', formatDateForUrlLocal(formData.checkOut));
    if (formData.guests) searchParams.set('guests', formData.guests);

    navigate(`/search?${searchParams.toString()}`);
  }, [getValues, trackSearchCompleted, navigate]);

  const hasValidDateRange = !!(checkIn && checkOut && checkIn < checkOut);

  return {
    checkIn,
    checkOut,
    guests,
    hasValidDateRange,

    form,

    handleCheckInChange,
    handleCheckOutChange,
    handleGuestsChange,
    handleSearch,
  };
};
